import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useMachine, useSelector } from '@xstate/react';
import { curriculumMachine } from '@edusense/entities-course';
import toast from 'react-hot-toast';
import { Button } from '@edusense/shared-ui-kit';
import { Select as HeroSelect, SelectItem } from '@heroui/react';
import { COURSE_EDIT_TEST_IDS } from '@edusense/shared-constants';
import { SafeDndProvider } from '@edusense/shared-utils';
import { ConfirmModal } from '@edusense/shared-ui-components';
// 추출된 컴포넌트들 import
import { CurriculumPanel } from './CurriculumPanel';
import { EditorPanel } from './EditorPanel';
import { ImportConfirmationDialog } from './ImportConfirmationDialog';
import { LectureSummaryContentTypeEnum, LectureSummaryPlatformSupportEnum } from './types';
import { parseMarkdownToCurriculumCustom } from './utils';
import { useCurriculumStore, ensureCurriculumStorePersistenceForCourse } from './hooks/useCurriculumStore';
const LANGUAGE_LABELS = {
    ko: '한국어',
    en: 'English',
    'en-us': 'English (US)',
    'en-gb': 'English (UK)',
    ja: '日本語',
    zh: '中文(간체)',
    'zh-cn': '中文(간체)',
    'zh-tw': '中文(번체)',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch'
};
const normalizeLanguageCode = (languageCode)=>{
    if (!languageCode) {
        return null;
    }
    const trimmed = languageCode.trim();
    if (!trimmed) {
        return null;
    }
    return trimmed.toLowerCase();
};
const getLanguageLabel = (languageCode)=>{
    const normalized = normalizeLanguageCode(languageCode) ?? '';
    if (!normalized) {
        return '';
    }
    return LANGUAGE_LABELS[normalized] ?? normalized.toUpperCase();
};
const cloneSectionsShallow = (sections = [])=>(sections || []).map((section)=>({
            ...section,
            lessons: (section.lessons || []).map((lesson)=>({
                    ...lesson
                }))
        }));
const buildSectionSignature = (sections = [])=>JSON.stringify((sections || []).map((section)=>({
            id: section.id,
            title: section.title,
            order: section.order ?? null,
            lessons: (section.lessons || []).map((lesson)=>({
                    id: lesson.id,
                    title: lesson.title,
                    order: lesson.order ?? null,
                    markdown: lesson.markdown ?? null,
                    isPreview: lesson.isPreview ?? false
                }))
        })));
// 스타일 import
import * as S from './CurriculumTabContent.styles';
/**
 * 커리큘럼 탭 컨텐츠 컴포넌트 (리팩토링됨)
 * 
 * 코스의 커리큘럼을 관리하는 메인 컴포넌트
 * 좌측 커리큘럼 패널과 우측 에디터 패널로 구성
 */ const CurriculumTabContent = /*#__PURE__*/ forwardRef(({ courseId, initialSections, localizedInitialSections, availableLanguages, defaultLanguage: defaultLanguageProp, activeLanguage, serverUpdatedAt, onFullScreenChange, isFullScreen, onDirtyStateChange }, ref)=>{
    const zustandInitializeSections = useCurriculumStore((state)=>state.initializeSections);
    const zustandAddSectionFromStoreDep = useCurriculumStore((state)=>state.addSection);
    const zustandDeleteSection = useCurriculumStore((state)=>state.deleteSection);
    const zustandUpdateSectionTitle = useCurriculumStore((state)=>state.updateSectionTitle);
    const zustandMoveSection = useCurriculumStore((state)=>state.moveSection);
    const zustandAddLessonFromStore = useCurriculumStore((state)=>state.addLesson);
    const zustandDeleteLesson = useCurriculumStore((state)=>state.deleteLesson);
    const zustandUpdateLesson = useCurriculumStore((state)=>state.updateLesson);
    const zustandSetSelectedLesson = useCurriculumStore((state)=>state.setSelectedLesson);
    const zustandMoveLesson = useCurriculumStore((state)=>state.moveLesson);
    const zustandPrimeLanguageSnapshot = useCurriculumStore((state)=>state.primeLanguageSnapshot);
    const zustandSwitchLanguage = useCurriculumStore((state)=>state.switchLanguage);
    const zustandGetLanguageSnapshots = useCurriculumStore((state)=>state.getLanguageSnapshots);
    const zustandSetSections = useCurriculumStore((state)=>state.setSections);
    const storeCurrentLanguage = useCurriculumStore((state)=>state.currentLanguage);
    const machineDependencies = useMemo(()=>({
            initializeSections: zustandInitializeSections,
            deleteSection: zustandDeleteSection,
            updateSectionTitle: zustandUpdateSectionTitle,
            moveSection: zustandMoveSection,
            addLesson: (sectionId, title)=>{
                const tempId = crypto.randomUUID();
                const newLessonToAdd = {
                    id: tempId,
                    title,
                    markdown: '',
                    contentType: LectureSummaryContentTypeEnum.Markdown,
                    platformSupport: LectureSummaryPlatformSupportEnum.IdeAndWeb
                };
                const selectedAfterAdd = zustandAddLessonFromStore(sectionId, newLessonToAdd);
                if (selectedAfterAdd) {
                    const currentStoreSections = useCurriculumStore.getState().sections;
                    const section = currentStoreSections.find((s)=>s.id === selectedAfterAdd.sectionId);
                    return section?.lessons.find((l)=>l.id === selectedAfterAdd.lessonId) || null;
                }
                return null;
            },
            deleteLesson: zustandDeleteLesson,
            updateLessonTitle: (sectionId, lessonId, title)=>{
                zustandUpdateLesson(sectionId, lessonId, {
                    title
                });
            },
            selectLesson: (sectionId, lessonId)=>{
                if (sectionId && lessonId) {
                    zustandSetSelectedLesson({
                        sectionId,
                        lessonId
                    });
                } else {
                    zustandSetSelectedLesson(null);
                }
            },
            moveLesson: zustandMoveLesson,
            importMarkdownToLesson: (sectionId, lessonId, markdownContent)=>{
                zustandUpdateLesson(sectionId, lessonId, {
                    markdown: markdownContent
                });
            },
            parseCurriculumMarkdown: parseMarkdownToCurriculumCustom,
            toastError: toast.error,
            toastSuccess: toast.success,
            confirmAction: (message)=>{
                if (pendingDeleteConfirmRef.current) {
                    pendingDeleteConfirmRef.current = false;
                    return true;
                }
                return window.confirm(message);
            },
            zustandAddSection: zustandAddSectionFromStoreDep,
            onCurriculumUpdated: (data)=>{
                console.log('[CurriculumTabContent] onCurriculumUpdated callback from machine:', data);
                if (onDirtyStateChange) {
                    onDirtyStateChange(data.isDirty);
                }
            },
            getSectionsFromStore: ()=>useCurriculumStore.getState().sections
        }), [
        zustandInitializeSections,
        zustandDeleteSection,
        zustandUpdateSectionTitle,
        zustandMoveSection,
        zustandAddLessonFromStore,
        zustandDeleteLesson,
        zustandUpdateLesson,
        zustandSetSelectedLesson,
        zustandMoveLesson,
        zustandAddSectionFromStoreDep,
        onDirtyStateChange
    ]);
    const [state, send, service] = useMachine(curriculumMachine, {
        input: {
            courseId: courseId,
            dependencies: machineDependencies
        }
    });
    const sanitizedAvailableLanguages = useMemo(()=>{
        const codes = new Set();
        (availableLanguages || []).forEach((code)=>{
            const normalized = normalizeLanguageCode(code);
            if (normalized) {
                codes.add(normalized);
            }
        });
        if (localizedInitialSections) {
            Object.keys(localizedInitialSections).forEach((code)=>{
                const normalized = normalizeLanguageCode(code);
                if (normalized) {
                    codes.add(normalized);
                }
            });
        }
        const normalizedDefault = normalizeLanguageCode(defaultLanguageProp);
        if (normalizedDefault) {
            codes.add(normalizedDefault);
        }
        if (codes.size === 0) {
            codes.add('ko');
        }
        return Array.from(codes);
    }, [
        availableLanguages,
        localizedInitialSections,
        defaultLanguageProp
    ]);
    const resolvedDefaultLanguage = useMemo(()=>{
        const normalizedDefault = normalizeLanguageCode(defaultLanguageProp);
        if (normalizedDefault && sanitizedAvailableLanguages.includes(normalizedDefault)) {
            return normalizedDefault;
        }
        return sanitizedAvailableLanguages[0] ?? 'ko';
    }, [
        defaultLanguageProp,
        sanitizedAvailableLanguages
    ]);
    const languageSectionsMap = useMemo(()=>{
        const map = {};
        if (localizedInitialSections) {
            Object.entries(localizedInitialSections).forEach(([code, sections])=>{
                const normalized = normalizeLanguageCode(code);
                if (!normalized) {
                    return;
                }
                map[normalized] = cloneSectionsShallow(sections || []);
            });
        }
        const normalizedDefault = resolvedDefaultLanguage ?? 'ko';
        if (!map[normalizedDefault]) {
            map[normalizedDefault] = cloneSectionsShallow(initialSections || []);
        }
        sanitizedAvailableLanguages.forEach((code)=>{
            if (!map[code]) {
                map[code] = [];
            }
        });
        return map;
    }, [
        localizedInitialSections,
        initialSections,
        resolvedDefaultLanguage,
        sanitizedAvailableLanguages
    ]);
    const languageSectionsSignature = useMemo(()=>{
        const sortedEntries = Object.entries(languageSectionsMap).sort(([a], [b])=>a.localeCompare(b));
        const payload = sortedEntries.map(([lang, sections])=>({
                lang,
                sections: (sections || []).map((section)=>({
                        id: section.id,
                        title: section.title,
                        order: section.order ?? null,
                        lessons: (section.lessons || []).map((lesson)=>({
                                id: lesson.id,
                                title: lesson.title,
                                order: lesson.order ?? null,
                                isPreview: lesson.isPreview ?? false
                            }))
                    }))
            }));
        return JSON.stringify(payload);
    }, [
        languageSectionsMap
    ]);
    const resolvedAvailableLanguages = useMemo(()=>{
        const codes = new Set();
        sanitizedAvailableLanguages.forEach((code)=>codes.add(code));
        Object.keys(languageSectionsMap).forEach((code)=>codes.add(code));
        const normalizedDefault = resolvedDefaultLanguage ?? 'ko';
        codes.add(normalizedDefault);
        return Array.from(codes);
    }, [
        languageSectionsMap,
        resolvedDefaultLanguage,
        sanitizedAvailableLanguages
    ]);
    const normalizedActiveLanguage = normalizeLanguageCode(activeLanguage);
    const effectiveCurrentLanguage = normalizeLanguageCode(storeCurrentLanguage) ?? normalizedActiveLanguage ?? resolvedDefaultLanguage;
    const activeLanguageForSelect = effectiveCurrentLanguage ?? resolvedDefaultLanguage;
    const defaultLanguageLabel = getLanguageLabel(resolvedDefaultLanguage ?? 'ko');
    const languageSelectOptions = useMemo(()=>resolvedAvailableLanguages.map((code)=>({
                code,
                label: getLanguageLabel(code)
            })), [
        resolvedAvailableLanguages
    ]);
    const canSwitchCurriculumLanguage = languageSelectOptions.length > 1;
    const activeLanguageKey = activeLanguageForSelect ?? languageSelectOptions[0]?.code ?? resolvedDefaultLanguage ?? 'ko';
    const isCurrentLanguageDefault = (normalizeLanguageCode(activeLanguageKey) ?? resolvedDefaultLanguage) === resolvedDefaultLanguage;
    const resolvedServerTimestamp = useMemo(()=>{
        if (serverUpdatedAt === undefined || serverUpdatedAt === null) {
            return null;
        }
        if (typeof serverUpdatedAt === 'number') {
            return Number.isFinite(serverUpdatedAt) ? serverUpdatedAt : null;
        }
        const parsed = Date.parse(serverUpdatedAt);
        return Number.isNaN(parsed) ? null : parsed;
    }, [
        serverUpdatedAt
    ]);
    const [isCoursePersistReady, setIsCoursePersistReady] = useState(false);
    const [sectionDeleteModalState, setSectionDeleteModalState] = useState({
        open: false,
        sectionId: null
    });
    const pendingDeleteConfirmRef = useRef(false);
    useEffect(()=>{
        let cancelled = false;
        if (!courseId) {
            setIsCoursePersistReady(false);
            return ()=>{
                cancelled = true;
            };
        }
        setIsCoursePersistReady(false);
        ensureCurriculumStorePersistenceForCourse(courseId).catch((error)=>{
            console.error('[CurriculumTabContent] Failed to prepare persistence for course:', courseId, error);
        }).finally(()=>{
            if (!cancelled) {
                setIsCoursePersistReady(true);
            }
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        courseId,
        ensureCurriculumStorePersistenceForCourse
    ]);
    useEffect(()=>{
        if (!courseId || !isCoursePersistReady) {
            return;
        }
        const snapshotsByLanguage = useCurriculumStore.getState().getLanguageSnapshots(courseId);
        Object.entries(languageSectionsMap).forEach(([lang, sections])=>{
            const normalizedLang = normalizeLanguageCode(lang);
            const existingSnapshot = normalizedLang ? snapshotsByLanguage[normalizedLang] ?? snapshotsByLanguage.default : snapshotsByLanguage.default;
            if (existingSnapshot) {
                const snapshotSignature = buildSectionSignature(existingSnapshot.sections || []);
                const incomingSignature = buildSectionSignature(sections || []);
                const hasDirtyLocalChanges = existingSnapshot.isCurriculumDirty;
                const localTimestamp = existingSnapshot.lastModifiedLocallyTimestamp;
                const hasNewerLocalTimestamp = localTimestamp !== null && (resolvedServerTimestamp === null || localTimestamp > resolvedServerTimestamp);
                if (hasDirtyLocalChanges || hasNewerLocalTimestamp || snapshotSignature !== incomingSignature) {
                    return;
                }
            }
            zustandPrimeLanguageSnapshot(courseId, lang, {
                sections,
                lastModifiedLocallyTimestamp: resolvedServerTimestamp ?? null,
                isCurriculumDirty: false
            });
        });
    }, [
        courseId,
        isCoursePersistReady,
        languageSectionsMap,
        languageSectionsSignature,
        resolvedServerTimestamp,
        zustandPrimeLanguageSnapshot
    ]);
    // XState 머신 초기화 (상단으로 이동됨)
    useEffect(()=>{
        if (!courseId || !isCoursePersistReady) {
            return;
        }
        const normalizedCurrent = normalizeLanguageCode(storeCurrentLanguage);
        if (normalizedCurrent && resolvedAvailableLanguages.includes(normalizedCurrent)) {
            return;
        }
        const fallbackLanguage = resolvedDefaultLanguage;
        if (normalizedCurrent === fallbackLanguage) {
            return;
        }
        const fallbackSections = languageSectionsMap[fallbackLanguage] || [];
        const fallbackSignature = JSON.stringify({
            language: fallbackLanguage,
            sections: fallbackSections.map((section)=>({
                    id: section.id,
                    title: section.title,
                    order: section.order ?? null,
                    lessons: (section.lessons || []).map((lesson)=>({
                            id: lesson.id,
                            title: lesson.title,
                            order: lesson.order ?? null,
                            markdown: lesson.markdown ?? null,
                            markdownUrl: lesson.markdownUrl ?? null,
                            videoUrl: lesson.videoUrl ?? null,
                            contentType: lesson.contentType ?? null,
                            platformSupport: lesson.platformSupport ?? null,
                            duration: lesson.duration ?? null,
                            isPreview: lesson.isPreview ?? false
                        }))
                }))
        });
        lastInitializationRef.current = {
            courseId,
            language: fallbackLanguage ?? 'default',
            signature: fallbackSignature
        };
        zustandSwitchLanguage(fallbackLanguage, {
            courseId,
            fallbackSections,
            providedTimestamp: resolvedServerTimestamp ?? null
        });
    }, [
        courseId,
        isCoursePersistReady,
        languageSectionsMap,
        resolvedAvailableLanguages,
        resolvedDefaultLanguage,
        resolvedServerTimestamp,
        storeCurrentLanguage,
        zustandSwitchLanguage
    ]);
    useEffect(()=>{
        if (!courseId || !isCoursePersistReady) {
            return;
        }
        const desiredLanguage = normalizedActiveLanguage ?? resolvedDefaultLanguage;
        if (!desiredLanguage) {
            return;
        }
        const normalizedCurrent = normalizeLanguageCode(storeCurrentLanguage);
        if (normalizedCurrent === desiredLanguage) {
            return;
        }
        const snapshots = useCurriculumStore.getState().getLanguageSnapshots(courseId);
        const snapshotEntry = snapshots[desiredLanguage] ?? snapshots.default;
        const fallbackSections = snapshotEntry?.sections ? cloneSectionsShallow(snapshotEntry.sections) : cloneSectionsShallow(languageSectionsMap[desiredLanguage] ?? []);
        zustandSwitchLanguage(desiredLanguage, {
            courseId,
            fallbackSections,
            providedTimestamp: snapshotEntry?.lastModifiedLocallyTimestamp ?? resolvedServerTimestamp ?? null
        });
    }, [
        courseId,
        isCoursePersistReady,
        languageSectionsMap,
        normalizedActiveLanguage,
        resolvedDefaultLanguage,
        resolvedServerTimestamp,
        storeCurrentLanguage,
        zustandSwitchLanguage
    ]);
    // 머신 컨텍스트에서 상태값들 선택적으로 구독
    const sections = useSelector(service, (snapshot)=>snapshot.context.sections);
    const curriculumMachineSelectedLesson = useSelector(service, (snapshot)=>snapshot.context.selectedLesson);
    const editingSectionId = useSelector(service, (snapshot)=>snapshot.context.editingSectionId);
    const editingSectionTitleCache = useSelector(service, (snapshot)=>snapshot.context.editingSectionTitleCache);
    const editingLessonId = useSelector(service, (snapshot)=>snapshot.context.editingLessonId);
    const editingLessonSectionId = useSelector(service, (snapshot)=>snapshot.context.editingLessonSectionId);
    const editingLessonTitleCache = useSelector(service, (snapshot)=>snapshot.context.editingLessonTitleCache);
    const isAddingSection = useSelector(service, (snapshot)=>snapshot.context.isAddingSection);
    const newSectionTitleField = useSelector(service, (snapshot)=>snapshot.context.newSectionTitleField);
    const addingLessonToSectionId = useSelector(service, (snapshot)=>snapshot.context.addingLessonToSectionId);
    const newLessonTitleField = useSelector(service, (snapshot)=>snapshot.context.newLessonTitleField);
    const parsedSectionsForImport = useSelector(service, (snapshot)=>snapshot.context.parsedSectionsForImport);
    // 로컬 상태
    const [isFullWidth, setIsFullWidth] = useState(isFullScreen || false);
    const [isCurriculumDropActive, setIsCurriculumDropActive] = useState(false);
    // Zustand 스토어 동기화
    const storeSectionsForSync = useCurriculumStore((state)=>state.sections);
    const prevStoreSectionsRef = useRef();
    const lastInitializationRef = useRef(null);
    useEffect(()=>{
        if (!courseId || !isCoursePersistReady) {
            return;
        }
        const targetLanguage = effectiveCurrentLanguage ?? resolvedDefaultLanguage;
        const sectionsForInitialization = cloneSectionsShallow(languageSectionsMap[targetLanguage] || []);
        const initializationSignature = JSON.stringify({
            language: targetLanguage,
            sections: sectionsForInitialization.map((section)=>({
                    id: section.id,
                    title: section.title,
                    order: section.order ?? null,
                    lessons: (section.lessons ?? []).map((lesson)=>({
                            id: lesson.id,
                            title: lesson.title,
                            order: lesson.order ?? null,
                            markdown: lesson.markdown ?? null,
                            markdownUrl: lesson.markdownUrl ?? null,
                            videoUrl: lesson.videoUrl ?? null,
                            contentType: lesson.contentType ?? null,
                            platformSupport: lesson.platformSupport ?? null,
                            duration: lesson.duration ?? null,
                            isPreview: lesson.isPreview ?? false
                        }))
                }))
        });
        const currentStore = useCurriculumStore.getState();
        const currentCourseInStore = currentStore.currentEditingCourseId;
        const currentLanguageInStore = normalizeLanguageCode(currentStore.currentLanguage);
        const lastInitialization = lastInitializationRef.current;
        const shouldInitialize = currentCourseInStore !== courseId || currentLanguageInStore !== targetLanguage || !lastInitialization || lastInitialization.courseId !== courseId || lastInitialization.language !== (targetLanguage ?? 'default') || lastInitialization.signature !== initializationSignature;
        if (!shouldInitialize) {
            return;
        }
        lastInitializationRef.current = {
            courseId,
            language: targetLanguage ?? 'default',
            signature: initializationSignature
        };
        zustandInitializeSections(sectionsForInitialization, courseId, {
            providedTimestamp: resolvedServerTimestamp ?? null,
            language: targetLanguage
        });
    }, [
        courseId,
        effectiveCurrentLanguage,
        isCoursePersistReady,
        languageSectionsMap,
        resolvedDefaultLanguage,
        resolvedServerTimestamp,
        zustandInitializeSections
    ]);
    useEffect(()=>{
        const currentSectionsString = JSON.stringify(storeSectionsForSync);
        const prevSectionsString = prevStoreSectionsRef.current ? JSON.stringify(prevStoreSectionsRef.current) : null;
        if (storeSectionsForSync && storeSectionsForSync.length > 0 && prevSectionsString !== currentSectionsString) {
            console.log('[CurriculumTabContent] Zustand sections updated, sending LOAD_FROM_STORE to machine. Count:', storeSectionsForSync.length);
            send({
                type: 'LOAD_FROM_STORE',
                sections: storeSectionsForSync,
                selectedLesson: useCurriculumStore.getState().selectedLesson,
                dirty: useCurriculumStore.getState().isCurriculumDirty
            });
        } else if (prevSectionsString !== null && prevSectionsString !== currentSectionsString && (!storeSectionsForSync || storeSectionsForSync.length === 0)) {
            console.log('[CurriculumTabContent] Zustand sections changed to empty, sending LOAD_FROM_STORE to machine.');
            send({
                type: 'LOAD_FROM_STORE',
                sections: [],
                selectedLesson: null,
                dirty: false
            });
        }
        prevStoreSectionsRef.current = storeSectionsForSync;
    }, [
        storeSectionsForSync,
        send
    ]);
    // 초기화
    useEffect(()=>{
        console.log('[CurriculumTabContent] Sending INITIALIZE_CURRICULUM. CourseId:', courseId, 'InitialSections prop count:', initialSections?.length);
        send({
            type: 'INITIALIZE_CURRICULUM',
            initialSections: initialSections || [],
            courseId
        });
    }, [
        initialSections,
        courseId,
        send
    ]);
    useEffect(()=>{
        setIsFullWidth(isFullScreen || false);
    }, [
        isFullScreen
    ]);
    // 강의 폼 dirty 상태 변경 핸들러
    const handleLectureFormDirtyChange = useCallback((isLectureDirty)=>{
        console.log("[CurriculumTabContent] LectureForm dirty state changed:", isLectureDirty);
        send({
            type: 'SET_LECTURE_FORM_DIRTY',
            isDirty: isLectureDirty
        });
    }, [
        send
    ]);
    // ref 핸들 구현
    useImperativeHandle(ref, ()=>({
            getSectionsDataForSave: ()=>{
                const store = useCurriculumStore.getState();
                const activeLanguageCode = normalizeLanguageCode(store.currentLanguage) ?? resolvedDefaultLanguage ?? null;
                const snapshots = store.getLanguageSnapshots(courseId);
                const localizedSections = {};
                Object.entries(snapshots).forEach(([langKey, snapshot])=>{
                    const normalizedKey = langKey === 'default' ? resolvedDefaultLanguage ?? 'ko' : langKey;
                    localizedSections[normalizedKey] = cloneSectionsShallow(snapshot.sections || []);
                });
                Object.entries(languageSectionsMap).forEach(([langKey, sections])=>{
                    if (!localizedSections[langKey]) {
                        localizedSections[langKey] = cloneSectionsShallow(sections);
                    }
                });
                resolvedAvailableLanguages.forEach((lang)=>{
                    if (!localizedSections[lang]) {
                        localizedSections[lang] = [];
                    }
                });
                if (activeLanguageCode) {
                    const currentSections = cloneSectionsShallow(store.sections || []);
                    localizedSections[activeLanguageCode] = currentSections;
                    return {
                        activeLanguage: activeLanguageCode,
                        sections: currentSections,
                        localizedSections
                    };
                }
                const fallbackLanguage = resolvedDefaultLanguage ?? resolvedAvailableLanguages[0] ?? null;
                const fallbackSections = fallbackLanguage ? localizedSections[fallbackLanguage] ?? cloneSectionsShallow(store.sections || []) : cloneSectionsShallow(store.sections || []);
                if (fallbackLanguage) {
                    localizedSections[fallbackLanguage] = fallbackSections;
                }
                return {
                    activeLanguage: fallbackLanguage ?? null,
                    sections: fallbackSections,
                    localizedSections
                };
            }
        }), [
        courseId,
        languageSectionsMap,
        resolvedAvailableLanguages,
        resolvedDefaultLanguage
    ]);
    // 이벤트 핸들러들
    const handleToggleAddSectionForm = ()=>send({
            type: 'TOGGLE_ADD_SECTION_FORM'
        });
    const handleSubmitAddSection = (e)=>{
        e.preventDefault();
        send({
            type: 'SUBMIT_ADD_SECTION'
        });
    };
    const handleSetNewSectionTitle = (title)=>send({
            type: 'SET_NEW_SECTION_TITLE',
            title
        });
    const handleStartEditSection = (sectionId)=>send({
            type: 'START_EDIT_SECTION',
            sectionId
        });
    const handleRequestDeleteSection = useCallback((sectionId)=>{
        setSectionDeleteModalState({
            open: true,
            sectionId
        });
    }, []);
    const handleConfirmDeleteSection = useCallback(()=>{
        setSectionDeleteModalState((prev)=>{
            if (prev.sectionId) {
                pendingDeleteConfirmRef.current = true;
                send({
                    type: 'DELETE_SECTION',
                    sectionId: prev.sectionId
                });
            }
            return {
                open: false,
                sectionId: null
            };
        });
    }, [
        send
    ]);
    const handleCancelDeleteSection = useCallback(()=>{
        setSectionDeleteModalState({
            open: false,
            sectionId: null
        });
    }, []);
    const handleSetEditingSectionTitle = (title)=>send({
            type: 'SET_EDITING_SECTION_TITLE',
            title
        });
    const handleSubmitEditSection = (e)=>{
        e?.preventDefault();
        if (editingSectionId) {
            send({
                type: 'SUBMIT_EDIT_SECTION',
                sectionId: editingSectionId
            });
        }
    };
    const handleToggleAddLessonForm = (sectionId)=>send({
            type: 'TOGGLE_ADD_LESSON_FORM',
            sectionId
        });
    const handleSubmitAddLesson = (e, sectionId)=>{
        e.preventDefault();
        send({
            type: 'SUBMIT_ADD_LESSON',
            sectionId
        });
    };
    const handleSetNewLessonTitle = (title)=>send({
            type: 'SET_NEW_LESSON_TITLE',
            title
        });
    const handleLessonSelect = (sectionId, lessonId)=>send({
            type: 'SELECT_LESSON',
            sectionId,
            lessonId
        });
    const handleStartEditLesson = (sectionId, lessonId)=>send({
            type: 'START_EDIT_LESSON',
            sectionId,
            lessonId
        });
    const handleDeleteLesson = (sectionId, lessonId)=>send({
            type: 'DELETE_LESSON',
            sectionId,
            lessonId
        });
    const handleSetEditingLessonTitle = (title)=>send({
            type: 'SET_EDITING_LESSON_TITLE',
            title
        });
    const handleSubmitEditLesson = (e)=>{
        e?.preventDefault();
        if (editingLessonSectionId && editingLessonId) {
            send({
                type: 'SUBMIT_EDIT_LESSON',
                sectionId: editingLessonSectionId,
                lessonId: editingLessonId
            });
        }
    };
    const handleMoveLesson = (sectionId, dragIndex, hoverIndex)=>send({
            type: 'MOVE_LESSON',
            sectionId,
            dragIndex,
            hoverIndex
        });
    // 무료 미리보기 토글 핸들러
    const handleTogglePreview = useCallback((sectionId, lessonId, isPreview)=>{
        zustandUpdateLesson(sectionId, lessonId, {
            isPreview
        });
    }, [
        zustandUpdateLesson
    ]);
    const handleLanguageChange = useCallback((keys)=>{
        const selectionArray = keys === 'all' ? languageSelectOptions.map((option)=>option.code) : Array.from(keys).map((key)=>key.toString());
        const nextLanguage = normalizeLanguageCode(selectionArray[0]);
        const activeLanguage = effectiveCurrentLanguage ?? resolvedDefaultLanguage;
        if (!courseId || !nextLanguage || nextLanguage === activeLanguage) {
            return;
        }
        const fallbackSections = languageSectionsMap[nextLanguage] || [];
        const signaturePayload = JSON.stringify({
            language: nextLanguage,
            sections: (fallbackSections || []).map((section)=>({
                    id: section.id,
                    title: section.title,
                    order: section.order ?? null,
                    lessons: (section.lessons || []).map((lesson)=>({
                            id: lesson.id,
                            title: lesson.title,
                            order: lesson.order ?? null,
                            markdown: lesson.markdown ?? null,
                            markdownUrl: lesson.markdownUrl ?? null,
                            videoUrl: lesson.videoUrl ?? null,
                            contentType: lesson.contentType ?? null,
                            platformSupport: lesson.platformSupport ?? null,
                            duration: lesson.duration ?? null,
                            isPreview: lesson.isPreview ?? false
                        }))
                }))
        });
        lastInitializationRef.current = {
            courseId,
            language: nextLanguage,
            signature: signaturePayload
        };
        zustandSwitchLanguage(nextLanguage, {
            courseId,
            fallbackSections,
            providedTimestamp: resolvedServerTimestamp ?? null
        });
    }, [
        courseId,
        effectiveCurrentLanguage,
        languageSectionsMap,
        languageSelectOptions,
        resolvedDefaultLanguage,
        resolvedServerTimestamp,
        zustandSwitchLanguage
    ]);
    const handleCopyFromDefaultLanguage = useCallback(()=>{
        if (!courseId) {
            return;
        }
        const activeLanguage = normalizeLanguageCode(storeCurrentLanguage) ?? resolvedDefaultLanguage;
        if (!activeLanguage || activeLanguage === resolvedDefaultLanguage) {
            toast.error('기본 언어에서는 복사가 필요하지 않습니다.');
            return;
        }
        const snapshots = zustandGetLanguageSnapshots(courseId);
        const baseSnapshot = snapshots[resolvedDefaultLanguage];
        if (!baseSnapshot || !Array.isArray(baseSnapshot.sections) || baseSnapshot.sections.length === 0) {
            toast.error('기본 언어 커리큘럼이 비어 있어 복사할 수 없습니다.');
            return;
        }
        const clonedSections = cloneSectionsShallow(baseSnapshot.sections || []);
        zustandSetSections(clonedSections);
        toast.success('기본 언어 커리큘럼을 현재 언어로 복사했습니다.');
    }, [
        courseId,
        resolvedDefaultLanguage,
        storeCurrentLanguage,
        zustandGetLanguageSnapshots,
        zustandSetSections
    ]);
    const processCurriculumMarkdownFile = useCallback(async (file)=>{
        if (!file) {
            toast.error('파일이 선택되지 않았습니다.');
            return;
        }
        if (!file.name.toLowerCase().endsWith('.md')) {
            toast.error('마크다운(.md) 파일만 업로드할 수 있습니다.');
            return;
        }
        try {
            const content = await file.text();
            if (!content.trim()) {
                toast.error('선택한 파일이 비어 있습니다.');
                return;
            }
            send({
                type: 'IMPORT_CURRICULUM_MARKDOWN_FILE',
                fileContent: content
            });
        } catch (error) {
            console.error('커리큘럼 파일 읽기 중 오류:', error);
            toast.error('커리큘럼 파일을 불러오지 못했습니다.');
        }
    }, [
        send
    ]);
    const handleCurriculumMarkdownImport = async (event)=>{
        const file = event.target.files?.[0];
        if (!file) {
            toast.error('파일이 선택되지 않았습니다.');
            return;
        }
        await processCurriculumMarkdownFile(file);
        if (event.target) {
            event.target.value = '';
        }
    };
    const handleCurriculumMarkdownDrop = useCallback(async (files)=>{
        if (!files || files.length === 0) {
            toast.error('드롭된 파일을 확인할 수 없습니다.');
            return;
        }
        await processCurriculumMarkdownFile(files[0]);
    }, [
        processCurriculumMarkdownFile
    ]);
    const handleCurriculumDropStateChange = useCallback((isActive)=>{
        setIsCurriculumDropActive(isActive);
    }, []);
    const handleMarkdownFileImport = async (event)=>{
        const file = event.target.files?.[0];
        if (!file || !curriculumMachineSelectedLesson) {
            toast.error('파일이 선택되지 않았거나 대상 강의가 없습니다.');
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e)=>{
            const content = e.target?.result;
            if (content) {
                send({
                    type: 'IMPORT_MARKDOWN_TO_LESSON',
                    sectionId: curriculumMachineSelectedLesson.sectionId,
                    lessonId: curriculumMachineSelectedLesson.lessonId,
                    fileContent: content
                });
            }
        };
        reader.readAsText(file);
        if (event.target) event.target.value = '';
    };
    const handleFullWidthToggle = ()=>{
        const newState = !isFullWidth;
        setIsFullWidth(newState);
        if (onFullScreenChange) {
            onFullScreenChange(newState);
        }
    };
    // 선택된 강의 데이터
    const selectedLessonData = curriculumMachineSelectedLesson ? sections.find((s)=>s.id === curriculumMachineSelectedLesson.sectionId)?.lessons.find((l)=>l.id === curriculumMachineSelectedLesson.lessonId) : null;
    // 로딩 상태
    if (state.matches('initializing')) {
        console.log('🔍 [CurriculumTabContent] Early return: initializing state');
        return /*#__PURE__*/ _jsx(S.Container, {
            $isFullWidth: isFullWidth,
            children: /*#__PURE__*/ _jsx("p", {
                children: "커리큘럼 로딩 중..."
            })
        });
    }
    // 커리큘럼 임포트 확인 상태
    if (state.matches('confirmingCurriculumImport') && parsedSectionsForImport && parsedSectionsForImport.length > 0) {
        console.log('🔍 [CurriculumTabContent] Early return: confirmingCurriculumImport state');
        return /*#__PURE__*/ _jsx(ImportConfirmationDialog, {
            isFullWidth: isFullWidth,
            parsedSectionsForImport: parsedSectionsForImport,
            onConfirm: ()=>send({
                    type: 'CONFIRM_CURRICULUM_IMPORT'
                }),
            onCancel: ()=>send({
                    type: 'CANCEL_CURRICULUM_IMPORT'
                })
        });
    }
    return /*#__PURE__*/ _jsxs(SafeDndProvider, {
        children: [
            /*#__PURE__*/ _jsxs(S.Container, {
                $isFullWidth: isFullWidth,
                children: [
                    /*#__PURE__*/ _jsxs(S.LanguageToolbar, {
                        "data-testid": COURSE_EDIT_TEST_IDS.CURRICULUM_TOOLBAR,
                        children: [
                            /*#__PURE__*/ _jsx(S.LanguageSelectGroup, {
                                children: /*#__PURE__*/ _jsxs(S.LanguageSelectWrapper, {
                                    "data-testid": COURSE_EDIT_TEST_IDS.CURRICULUM_LANGUAGE_SELECT_WRAPPER,
                                    children: [
                                        /*#__PURE__*/ _jsx(S.LanguageLabel, {
                                            children: "편집 언어"
                                        }),
                                        canSwitchCurriculumLanguage ? /*#__PURE__*/ _jsx(HeroSelect, {
                                            "aria-label": "커리큘럼 편집 언어 선택",
                                            selectionMode: "single",
                                            selectedKeys: new Set([
                                                activeLanguageKey
                                            ]),
                                            onSelectionChange: handleLanguageChange,
                                            placeholder: "편집 언어 선택",
                                            "data-testid": `${COURSE_EDIT_TEST_IDS.CURRICULUM_LANGUAGE_SELECT_WRAPPER}-trigger`,
                                            children: languageSelectOptions.map((option)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: option.code,
                                                    "data-testid": `${COURSE_EDIT_TEST_IDS.CURRICULUM_LANGUAGE_OPTION_PREFIX}-${option.code}`,
                                                    children: option.label
                                                }, option.code))
                                        }) : /*#__PURE__*/ _jsx(S.LanguageStaticValue, {
                                            "aria-live": "polite",
                                            children: getLanguageLabel(activeLanguageKey)
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs(S.LanguageActionGroup, {
                                "data-testid": COURSE_EDIT_TEST_IDS.CURRICULUM_LANGUAGE_COPY_GROUP,
                                children: [
                                    /*#__PURE__*/ _jsx(S.LanguageLabel, {
                                        children: "기본 언어"
                                    }),
                                    /*#__PURE__*/ _jsxs(S.LanguageHelper, {
                                        children: [
                                            "기본 언어: ",
                                            defaultLanguageLabel
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        size: "sm",
                                        variant: "outline",
                                        onClick: handleCopyFromDefaultLanguage,
                                        disabled: isCurrentLanguageDefault,
                                        "data-testid": COURSE_EDIT_TEST_IDS.CURRICULUM_LANGUAGE_COPY_BUTTON,
                                        children: "기본 언어 커리큘럼 복사"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CurriculumPanel, {
                        sections: sections,
                        isFullWidth: isFullWidth,
                        isAddingSection: isAddingSection,
                        newSectionTitleField: newSectionTitleField,
                        editingSectionId: editingSectionId,
                        editingSectionTitleCache: editingSectionTitleCache,
                        addingLessonToSectionId: addingLessonToSectionId,
                        newLessonTitleField: newLessonTitleField,
                        curriculumMachineSelectedLesson: curriculumMachineSelectedLesson,
                        editingLessonSectionId: editingLessonSectionId,
                        editingLessonId: editingLessonId,
                        editingLessonTitleCache: editingLessonTitleCache,
                        onToggleAddSectionForm: handleToggleAddSectionForm,
                        onSubmitAddSection: handleSubmitAddSection,
                        onSetNewSectionTitle: handleSetNewSectionTitle,
                        onCurriculumMarkdownImport: handleCurriculumMarkdownImport,
                        onStartEditSection: handleStartEditSection,
                        onToggleAddLessonForm: handleToggleAddLessonForm,
                        onDeleteSection: handleRequestDeleteSection,
                        onSubmitEditSection: handleSubmitEditSection,
                        onSubmitAddLesson: handleSubmitAddLesson,
                        onSetEditingSectionTitle: handleSetEditingSectionTitle,
                        onSetNewLessonTitle: handleSetNewLessonTitle,
                        onLessonSelect: handleLessonSelect,
                        onStartEditLesson: handleStartEditLesson,
                        onDeleteLesson: handleDeleteLesson,
                        onSubmitEditLesson: handleSubmitEditLesson,
                        onSetEditingLessonTitle: handleSetEditingLessonTitle,
                        onMoveLesson: handleMoveLesson,
                        onTogglePreview: handleTogglePreview,
                        onCurriculumMarkdownDrop: handleCurriculumMarkdownDrop,
                        onCurriculumDropStateChange: handleCurriculumDropStateChange,
                        isDragActive: isCurriculumDropActive,
                        send: send
                    }),
                    console.log('🔍 [CurriculumTabContent] About to render EditorPanel with:', {
                        isFullWidth,
                        curriculumMachineSelectedLesson,
                        selectedLessonData,
                        courseId,
                        hasSelectedLesson: !!curriculumMachineSelectedLesson
                    }),
                    /*#__PURE__*/ _jsx(EditorPanel, {
                        isFullWidth: isFullWidth,
                        curriculumMachineSelectedLesson: curriculumMachineSelectedLesson,
                        selectedLessonData: selectedLessonData,
                        courseId: courseId,
                        onBackToCurriculum: handleFullWidthToggle,
                        onMarkdownFileImport: handleMarkdownFileImport,
                        onLectureFormDirtyChange: handleLectureFormDirtyChange
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: sectionDeleteModalState.open,
                title: "섹션 삭제",
                message: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "이 섹션을 삭제하면 포함된 모든 강의도 함께 삭제됩니다."
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            children: "정말로 삭제하시겠습니까?"
                        })
                    ]
                }),
                confirmText: "삭제",
                cancelText: "취소",
                isDangerous: true,
                onConfirm: handleConfirmDeleteSection,
                onCancel: handleCancelDeleteSection
            })
        ]
    });
});
export default CurriculumTabContent;
